import React, { useEffect, useState, useReducer } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LINES = [
  { text: "Queixa principal: Lombalgia crônica inespecífica, EVA 7/10..." },
  { text: "Avaliação funcional: Limitação de flexão anterior 60°..." },
  { text: "Hipótese diagnóstica: Síndrome facetária L4-L5..." },
  {
    text: "Plano terapêutico: 12 sessões · McKenzie · Estabilização segmentar",
    badge: "Sugestão da IA · aguardando sua validação",
  },
];

// Phases:
//  0 -> waves (0..2s)
//  1 -> lines appearing (2..4s)
//  2 -> lines approved/checkmark (4..4.5s)
//  3 -> hold then fade back (5..8s)

export default function HeroAnimation() {
  const [phase, setPhase] = useState(0);
  const [, force] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        setPhase(0);
        await wait(2000);
        if (cancelled) return;
        setPhase(1);
        await wait(2200);
        if (cancelled) return;
        setPhase(2);
        await wait(2800);
        if (cancelled) return;
        setPhase(3);
        await wait(800);
        if (cancelled) return;
        force();
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const showWaves = phase === 0 || phase === 3;
  const showLines = phase === 1 || phase === 2;
  const showCheck = phase === 2;

  return (
    <div className="hero-animation-wrap" data-testid="hero-animation">
      <div className="hero-animation glass">
        <AnimatePresence mode="wait">
          {showWaves && (
            <motion.div
              key="waves"
              className="hero-animation-waves"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <WaveSVG />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showLines && (
            <motion.div
              key="lines"
              className="hero-animation-lines"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {LINES.map((line, idx) => (
                <motion.div
                  key={line.text}
                  className="hero-animation-line"
                  data-testid={`hero-animation-line-${idx}`}
                  initial={{ opacity: 0, x: -8, borderLeftColor: "rgba(241,136,89,0)" }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    borderLeftColor: [
                      "rgba(241,136,89,0)",
                      "rgba(241,136,89,1)",
                      "rgba(241,136,89,0.2)",
                    ],
                  }}
                  transition={{
                    delay: 0.3 * idx,
                    duration: 0.5,
                    times: [0, 0.4, 1],
                  }}
                >
                  <span className="hero-animation-text">
                    {line.text}
                    {line.badge && (
                      <span className="hero-animation-badge" data-testid="hero-animation-ai-badge">
                        {line.badge}
                      </span>
                    )}
                  </span>
                  {showCheck && (
                    <motion.span
                      className="hero-animation-check"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * idx, duration: 0.3 }}
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <p className="mono-xs hero-animation-caption">
        Demonstração do fluxo real · Revisão humana obrigatória antes de salvar
      </p>
    </div>
  );
}

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function WaveSVG() {
  return (
    <svg viewBox="0 0 600 160" preserveAspectRatio="none" aria-hidden="true">
      <motion.path
        d="M0,80 C100,30 200,130 300,80 C400,30 500,130 600,80"
        fill="none"
        stroke="#7B417B"
        strokeOpacity="0.6"
        strokeWidth="2"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1],
          opacity: [0, 0.6, 0.6],
        }}
        transition={{ duration: 1.6, times: [0, 0.6, 1] }}
      />
      <motion.path
        d="M0,80 C75,40 150,120 225,80 C300,40 375,120 450,80 C525,40 600,120 600,80"
        fill="none"
        stroke="#4C356B"
        strokeOpacity="0.8"
        strokeWidth="2"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1],
          opacity: [0, 0.8, 0.8],
        }}
        transition={{ duration: 1.6, delay: 0.15, times: [0, 0.6, 1] }}
      />
      <motion.path
        d="M0,80 C120,55 240,105 360,80 C480,55 540,105 600,80"
        fill="none"
        stroke="#F18859"
        strokeOpacity="0.4"
        strokeWidth="2"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1],
          opacity: [0, 0.4, 0.4],
        }}
        transition={{ duration: 1.6, delay: 0.3, times: [0, 0.6, 1] }}
      />
    </svg>
  );
}
